import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const MEDIUMS = [
  "Abstract Canvas (Mixed Media)",
  "Atmospheric Landscape (Oil & Cold Wax)",
  "Textured Acrylic on Linen",
  "Mineral Pigment on Board",
];

const inputCls =
  "w-full bg-ground border border-line-light px-3 py-2 text-sm text-white focus:outline-none focus:border-gold";
const labelCls = "block text-xs mono-tag uppercase text-slate-400 mb-1";

export default function AddPiece({ onAdded }) {
  const [form, setForm] = useState({
    title: "",
    price: "",
    medium: MEDIUMS[0],
    dimensions: "",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPEG, PNG, TIFF)");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    const price = parseFloat(String(form.price).replace(/[^0-9.]/g, ""));
    if (!form.title.trim() || !price || !form.dimensions.trim()) {
      toast.error("Title, price and dimensions are required");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("title", form.title.trim());
      data.append("price", price);
      data.append("medium", form.medium);
      data.append("dimensions", form.dimensions.trim());
      if (file) data.append("image", file);
      const res = await api.post("/artworks", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${res.data.title}" is live in your gallery`);
      setForm({ title: "", price: "", medium: MEDIUMS[0], dimensions: "" });
      setFile(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      onAdded?.(res.data);
      document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish artwork");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="add-piece" className="py-20 border-b border-line bg-ground-alt">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="mono-tag text-xs uppercase text-gold tracking-widest block mb-2">
              Artist Studio Control
            </span>
            <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
              Add Your Pieces
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              Publish your newest abstract or landscape paintings directly to
              your gallery. Upload canvas photography, set pricing, dimensions,
              and enable buy buttons.
            </p>
            <div className="p-4 bg-ground-elevated border border-line space-y-3">
              {[
                "Instant live listing in your catalog",
                "Direct checkout buy button attached",
                "Custom medium & dimension badges",
              ].map((t) => (
                <div key={t} className="flex items-center gap-3 text-xs text-slate-300">
                  <span className="text-gold font-bold">✓</span> {t}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 bg-ground-card p-8 border border-line-light">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-line">
              <h3 className="text-base font-bold uppercase text-white">
                Artwork Upload &amp; Listing Form
              </h3>
              <span className="mono-tag text-[11px] text-gold uppercase">
                Live Publisher
              </span>
            </div>

            <form className="space-y-4" onSubmit={submit} data-testid="add-piece-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="piece-title">Artwork Title</label>
                  <input
                    id="piece-title"
                    data-testid="add-piece-title-input"
                    type="text"
                    className={inputCls}
                    placeholder="e.g. Celestial Horizon VIII"
                    value={form.title}
                    onChange={set("title")}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="piece-price">Price (USD)</label>
                  <input
                    id="piece-price"
                    data-testid="add-piece-price-input"
                    type="text"
                    inputMode="decimal"
                    className={inputCls}
                    placeholder="e.g. $2,800"
                    value={form.price}
                    onChange={set("price")}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="piece-medium">Medium / Category</label>
                  <select
                    id="piece-medium"
                    data-testid="add-piece-medium-select"
                    className={inputCls}
                    value={form.medium}
                    onChange={set("medium")}
                  >
                    {MEDIUMS.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="piece-dimensions">Dimensions (H × W)</label>
                  <input
                    id="piece-dimensions"
                    data-testid="add-piece-dimensions-input"
                    type="text"
                    className={inputCls}
                    placeholder="e.g. 48 × 36 in"
                    value={form.dimensions}
                    onChange={set("dimensions")}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Artwork Photography</label>
                <div
                  className="border-2 border-dashed border-line-light hover:border-gold transition-colors p-6 bg-ground text-center cursor-pointer"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    pickFile(e.dataTransfer.files?.[0]);
                  }}
                  data-testid="add-piece-dropzone"
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="Artwork preview"
                      className="mx-auto max-h-48 object-contain mb-2"
                      data-testid="add-piece-preview"
                    />
                  ) : (
                    <svg
                      className="w-8 h-8 mx-auto text-gold mb-2"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                  )}
                  <p className="text-xs text-white font-semibold uppercase">
                    {file ? file.name : "Drop high-res canvas photography here"}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1 mono-tag">
                    JPEG, PNG, TIFF up to 12MB
                  </p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="add-piece-image-input"
                    onChange={(e) => pickFile(e.target.files?.[0])}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold-hover transition-colors disabled:opacity-60"
                data-testid="add-piece-submit-button"
              >
                {submitting ? "Publishing…" : "+ Publish Artwork to Gallery"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
